/**
 * Notification Service - Powered by Sonner
 * Modern toast notifications with superior UX
 */

import { toast } from 'sonner';

class NotificationService {
  success(message: string, options?: { description?: string; duration?: number }) {
    return toast.success(message, {
      description: options?.description,
      duration: options?.duration || 4000,
    });
  }

  error(message: string, options?: { description?: string; duration?: number }) {
    return toast.error(message, {
      description: options?.description,
      duration: options?.duration || 5000,
    });
  }

  info(message: string, options?: { description?: string; duration?: number }) {
    return toast.info(message, {
      description: options?.description,
      duration: options?.duration || 4000,
    });
  }

  warning(message: string, options?: { description?: string; duration?: number }) {
    return toast.warning(message, {
      description: options?.description,
      duration: options?.duration || 4000,
    });
  }

  loading(message: string, options?: { description?: string }) {
    return toast.loading(message, {
      description: options?.description,
    });
  }

  // Powerful promise support - great for API calls
  promise<T>(
    promise: Promise<T>,
    {
      loading,
      success,
      error,
    }: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    }
  ) {
    return toast.promise(promise, { loading, success, error });
  }

  // Action buttons - for undo/retry functionality
  action(
    message: string,
    options: {
      action: { label: string; onClick: () => void };
      description?: string;
      duration?: number;
    }
  ) {
    return toast(message, {
      description: options.description,
      duration: options.duration || 4000,
      action: options.action,
    });
  }

  // Custom toast with JSX content
  custom(content: React.ReactNode, options?: { duration?: number }) {
    return toast.custom(() => content as React.ReactElement, {
      duration: options?.duration || 4000,
    });
  }

  dismiss(toastId?: string | number) {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  }
}

export const notificationService = new NotificationService();
